import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    Link as MuiLink,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import apiService from '../services/api.service';
import type {
    APSubmitInput,
    PaymentMethod,
    PaymentRequestStatus,
    PaymentRequestWithChildren,
    ReConfirmedYesNo,
} from '../types/paymentRequest';
import PaymentRequestStatusChip from './components/PaymentRequestStatusChip';
import PaymentRequestAttachments from './components/PaymentRequestAttachments';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPROVER_ROLES = ['ChiefAccountant', 'FinanceManager', 'GeneralManager', 'CEO'] as const;
const APPROVER_ELIGIBLE_STATUSES: PaymentRequestStatus[] = [
    'Submitted',
    'APPrepared',
    'CAReviewed',
    'FMApproved',
    'GMApproved',
];

const OVERRIDE_FIELDS: { value: string; label: string }[] = [
    { value: 'invoiceNumber', label: 'Invoice Number' },
    { value: 'invoiceDate', label: 'Invoice Date' },
    { value: 'amountFigures', label: 'Amount Figures' },
    { value: 'amountCurrency', label: 'Amount Currency' },
    { value: 'payBy', label: 'Pay By' },
    { value: 'paymentMethod', label: 'Payment Method' },
    { value: 'bankName', label: 'Bank Name' },
    { value: 'bankAddress', label: 'Bank Address' },
    { value: 'bankAccountNo', label: 'Bank Account No' },
    { value: 'bankIBAN', label: 'Bank IBAN' },
    { value: 'bankSWIFT', label: 'Bank SWIFT' },
    { value: 'bankCurrency', label: 'Bank Currency' },
    { value: 'bankEmail', label: 'Bank Email' },
    { value: 'bankTel', label: 'Bank Tel' },
    { value: 'bankFax', label: 'Bank Fax' },
    { value: 'reConfirmedYesNo', label: 'Re-Confirmed?' },
    { value: 'reConfirmerName', label: 'Re-Confirmer Name' },
    { value: 'reConfirmerEmpID', label: 'Re-Confirmer Emp ID' },
    { value: 'initiatorRemarks', label: 'Initiator Remarks' },
    { value: 'apRemarks', label: 'AP Remarks' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
    if (!value) return '—';
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
};

const formatMoney = (amount: number | string | null | undefined, currency: string | null | undefined): string => {
    const n = Number(amount ?? 0);
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
    return currency ? `${formatted} ${currency}` : formatted;
};

const errMsg = (err: any, fallback: string): string =>
    err?.response?.data?.message || err?.message || fallback;

interface ReadOnlyFieldProps {
    label: string;
    value: React.ReactNode;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {value === null || value === undefined || value === '' ? '—' : value}
        </Typography>
    </Box>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PaymentRequestDetail: React.FC = () => {
    const navigate = useNavigate();
    const { id: idParam } = useParams<{ id: string }>();
    const id = Number(idParam);
    const idValid = Number.isFinite(id) && id > 0;

    const user = useSelector((s: RootState) => s.auth.user) as
        | { userid?: number; userId?: number; role?: string; fullname?: string; firstname?: string; lastname?: string }
        | null;
    const userRole = user?.role || '';
    const isAPTeam = userRole === 'APTeam';
    const isAdmin = userRole === 'Admin';
    const isApproverRole = (APPROVER_ROLES as readonly string[]).includes(userRole);

    const [pr, setPR] = useState<PaymentRequestWithChildren | null>(null);
    const [docusignStatus, setDocusignStatus] = useState<any | null>(null);
    const [docusignLoading, setDocusignLoading] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionSuccess, setActionSuccess] = useState<string | null>(null);

    // ─── AP prep state ────────────────────────────────────────────────────
    const [apForm, setAPForm] = useState<APSubmitInput>({
        bankName: '',
        bankAddress: '',
        bankAccountNo: '',
        bankIBAN: '',
        bankSWIFT: '',
        bankCurrency: '',
        bankEmail: '',
        bankTel: '',
        bankFax: '',
        reConfirmedYesNo: 'NotApplicable',
        reConfirmerName: '',
        reConfirmerEmpID: '',
        paymentMethod: 'Transfer',
        apRemarks: '',
        invoiceNumber: '',
        invoiceDate: '',
    });
    const [apSubmitting, setAPSubmitting] = useState(false);
    const [returnDialogOpen, setReturnDialogOpen] = useState(false);
    const [returnComments, setReturnComments] = useState('');

    // ─── Approver action state ─────────────────────────────────────────────
    const [approverComments, setApproverComments] = useState('');
    const [approverBusy, setApproverBusy] = useState(false);

    // ─── Post-to-SAP state ─────────────────────────────────────────────────
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewPayload, setPreviewPayload] = useState<any>(null);
    const [postConfirmOpen, setPostConfirmOpen] = useState(false);
    const [postingSAP, setPostingSAP] = useState(false);

    // ─── Override state ────────────────────────────────────────────────────
    const [overrideExpanded, setOverrideExpanded] = useState(false);
    const [overrideField, setOverrideField] = useState<string>('invoiceNumber');
    const [overrideValue, setOverrideValue] = useState<string>('');
    const [overrideReason, setOverrideReason] = useState<string>('');
    const [overrideBusy, setOverrideBusy] = useState(false);

    // -----------------------------------------------------------------------
    // Fetch
    // -----------------------------------------------------------------------

    const refresh = useCallback(async () => {
        if (!idValid) return;
        try {
            setLoading(true);
            setPageError(null);
            const res = await apiService.getPaymentRequest(id);
            const data: PaymentRequestWithChildren = res?.data ?? res;
            setPR(data);
            // Hydrate AP form with current (possibly null) values once we have data.
            setAPForm((prev) => ({
                ...prev,
                bankName: data?.bankName ?? prev.bankName ?? '',
                bankAddress: data?.bankAddress ?? prev.bankAddress ?? '',
                bankAccountNo: data?.bankAccountNo ?? prev.bankAccountNo ?? '',
                bankIBAN: data?.bankIBAN ?? prev.bankIBAN ?? '',
                bankSWIFT: data?.bankSWIFT ?? prev.bankSWIFT ?? '',
                bankCurrency: data?.bankCurrency ?? prev.bankCurrency ?? '',
                bankEmail: data?.bankEmail ?? prev.bankEmail ?? '',
                bankTel: data?.bankTel ?? prev.bankTel ?? '',
                bankFax: data?.bankFax ?? prev.bankFax ?? '',
                reConfirmedYesNo:
                    (data?.reConfirmedYesNo as ReConfirmedYesNo) || prev.reConfirmedYesNo || 'NotApplicable',
                reConfirmerName: data?.reConfirmerName ?? prev.reConfirmerName ?? '',
                reConfirmerEmpID: data?.reConfirmerEmpID ?? prev.reConfirmerEmpID ?? '',
                paymentMethod: (data?.paymentMethod as PaymentMethod) || prev.paymentMethod || 'Transfer',
                apRemarks: data?.apRemarks ?? prev.apRemarks ?? '',
                invoiceNumber: data?.invoiceNumber ?? prev.invoiceNumber ?? '',
                invoiceDate:
                    data?.invoiceDate
                        ? String(data.invoiceDate).slice(0, 10)
                        : (prev.invoiceDate ?? ''),
            }));
        } catch (err: any) {
            setPageError(errMsg(err, 'Failed to load payment request.'));
        } finally {
            setLoading(false);
        }
    }, [id, idValid]);

    useEffect(() => {
        if (!idValid) {
            setPageError('Invalid payment request id.');
            setLoading(false);
            return;
        }
        refresh();
    }, [idValid, refresh]);

    // -----------------------------------------------------------------------
    // DocuSign envelope status (local mirror — works even when JWT keys
    // are missing from the server, because we read from docusignenvelopes /
    // docusignsigners that are populated by the webhook + polling sync).
    // -----------------------------------------------------------------------

    const refreshDocuSignStatus = useCallback(async () => {
        if (!pr?.paymentRequestId || !pr.docuSignEnvelopeId) {
            setDocusignStatus(null);
            return;
        }
        setDocusignLoading(true);
        try {
            const res = await apiService.getPaymentRequestDocuSignStatus(pr.paymentRequestId);
            setDocusignStatus(res?.data ?? null);
        } catch {
            setDocusignStatus(null);
        } finally {
            setDocusignLoading(false);
        }
    }, [pr?.paymentRequestId, pr?.docuSignEnvelopeId]);

    useEffect(() => {
        refreshDocuSignStatus();
    }, [refreshDocuSignStatus]);

    // -----------------------------------------------------------------------
    // Derived flags
    // -----------------------------------------------------------------------

    const status: PaymentRequestStatus | undefined = pr?.status;
    const showAPPrep = isAPTeam && status === 'Submitted';

    // Attachments — who can upload + what stage tag to use.
    const userId = user?.userid ?? user?.userId ?? null;
    const isInitiatorOfThisPR = pr?.initiatorUserId === userId;
    const TERMINAL_STATUSES_LOCAL = ['Rejected', 'Voided', 'Executed'];
    const isTerminal = !!status && TERMINAL_STATUSES_LOCAL.includes(status);
    const attachmentsCanEdit =
        !isTerminal &&
        (
            (isInitiatorOfThisPR && (status === 'Draft' || status === 'Returned')) ||
            (isAPTeam && status === 'Submitted') ||
            isAdmin
        );
    const attachmentsDefaultStage: 'Initiator' | 'AP' | 'CA' | 'FM' | 'GM' | 'CEO' | 'System' =
        isInitiatorOfThisPR && (status === 'Draft' || status === 'Returned') ? 'Initiator'
            : isAPTeam ? 'AP'
            : userRole === 'ChiefAccountant' ? 'CA'
            : userRole === 'FinanceManager' ? 'FM'
            : userRole === 'GeneralManager' ? 'GM'
            : userRole === 'CEO' ? 'CEO'
            : 'System';
    const showApproverPanel =
        isApproverRole &&
        !!status &&
        APPROVER_ELIGIBLE_STATUSES.includes(status);
    const showPostToSAP = isAPTeam && status === 'FinalApproved';
    const docusignPending = !!pr?.docuSignEnvelopeId && isApproverRole;

    const submitButtonLabel = useMemo(() => {
        // Three flavours, in order of specificity:
        //   1. Envelope already exists → handing off to next signer.
        //   2. DocuSign company, envelope not yet created → AP-submit will
        //      create the envelope. Spec § 8.4 wording.
        //   3. Non-DocuSign company → no envelope ever; just hand to CA via
        //      the in-app chain. Anything mentioning "signature" would be
        //      misleading here.
        // docuSignEnabled === undefined (older cached payloads) falls back to
        // the legacy DocuSign-default wording so existing companies behave
        // exactly as before this change rolls out.
        const envelopeExists = !!pr?.docuSignEnvelopeId;
        if (envelopeExists) return 'Submit to Chief Accountant';
        const docuSignActive = pr?.docuSignEnabled !== false;
        if (docuSignActive) return 'Submit & Send for Signature';
        return 'Submit to Chief Accountant';
    }, [pr?.docuSignEnvelopeId, pr?.docuSignEnabled]);

    // -----------------------------------------------------------------------
    // Action handlers
    // -----------------------------------------------------------------------

    const clearActionMessages = () => {
        setActionError(null);
        setActionSuccess(null);
    };

    const handleAPSubmit = async () => {
        if (!pr) return;
        if (apForm.reConfirmedYesNo === 'Yes') {
            if (!apForm.reConfirmerName?.trim() || !apForm.reConfirmerEmpID?.trim()) {
                setActionError('Re-Confirmer Name and Emp ID are required when Re-Confirmation = Yes.');
                return;
            }
        }
        try {
            clearActionMessages();
            setAPSubmitting(true);
            await apiService.apSubmitPaymentRequest(pr.paymentRequestId, apForm);
            setActionSuccess('AP submission saved.');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'AP submit failed.'));
        } finally {
            setAPSubmitting(false);
        }
    };

    const handleReturn = async () => {
        if (!pr) return;
        if (!returnComments.trim()) {
            setActionError('Comments are required to return a payment request.');
            return;
        }
        try {
            clearActionMessages();
            setAPSubmitting(true);
            await apiService.returnPaymentRequest(pr.paymentRequestId, returnComments.trim());
            setActionSuccess('Returned to initiator.');
            setReturnDialogOpen(false);
            setReturnComments('');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Return failed.'));
        } finally {
            setAPSubmitting(false);
        }
    };

    const handleApprove = async () => {
        if (!pr) return;
        try {
            clearActionMessages();
            setApproverBusy(true);
            await apiService.approvePaymentRequest(pr.paymentRequestId, approverComments.trim() || undefined);
            setActionSuccess('Approved.');
            setApproverComments('');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Approve failed.'));
        } finally {
            setApproverBusy(false);
        }
    };

    const handleReject = async () => {
        if (!pr) return;
        if (!approverComments.trim()) {
            setActionError('Comments are required to reject.');
            return;
        }
        try {
            clearActionMessages();
            setApproverBusy(true);
            await apiService.rejectPaymentRequest(pr.paymentRequestId, approverComments.trim());
            setActionSuccess('Rejected.');
            setApproverComments('');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Reject failed.'));
        } finally {
            setApproverBusy(false);
        }
    };

    const handleApproverReturn = async () => {
        if (!pr) return;
        if (!approverComments.trim()) {
            setActionError('Comments are required to return.');
            return;
        }
        try {
            clearActionMessages();
            setApproverBusy(true);
            await apiService.returnPaymentRequest(pr.paymentRequestId, approverComments.trim());
            setActionSuccess('Returned.');
            setApproverComments('');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Return failed.'));
        } finally {
            setApproverBusy(false);
        }
    };

    const handlePreview = async () => {
        if (!pr) return;
        try {
            clearActionMessages();
            setPreviewLoading(true);
            const res = await apiService.previewPaymentRequestSAP(pr.paymentRequestId);
            setPreviewPayload(res?.data ?? res);
            setPreviewOpen(true);
        } catch (err: any) {
            setActionError(errMsg(err, 'Preview failed.'));
        } finally {
            setPreviewLoading(false);
        }
    };

    const handlePostToSAP = async () => {
        if (!pr) return;
        try {
            clearActionMessages();
            setPostingSAP(true);
            await apiService.postPaymentRequestToSAP(pr.paymentRequestId);
            setActionSuccess('Posted to SAP.');
            setPostConfirmOpen(false);
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Post to SAP failed.'));
        } finally {
            setPostingSAP(false);
        }
    };

    const handleRetrySAP = async () => {
        if (!pr) return;
        try {
            clearActionMessages();
            setPostingSAP(true);
            await apiService.retryPaymentRequestSAP(pr.paymentRequestId);
            setActionSuccess('SAP retry succeeded.');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'SAP retry failed.'));
        } finally {
            setPostingSAP(false);
        }
    };

    const handleOverrideSubmit = async () => {
        if (!pr) return;
        if (!overrideField || !overrideReason.trim()) {
            setActionError('Field and reason are required for override.');
            return;
        }
        try {
            clearActionMessages();
            setOverrideBusy(true);
            await apiService.overridePaymentRequest(
                pr.paymentRequestId,
                overrideField,
                overrideValue || null,
                overrideReason.trim(),
            );
            setActionSuccess(`Override applied to ${overrideField}.`);
            setOverrideValue('');
            setOverrideReason('');
            await refresh();
        } catch (err: any) {
            setActionError(errMsg(err, 'Override failed.'));
        } finally {
            setOverrideBusy(false);
        }
    };

    const [submitBusy, setSubmitBusy] = useState(false);
    const handleInitiatorSubmit = async () => {
        if (!pr || submitBusy) return;
        if (!window.confirm('Submit this Payment Request to the AP team? Once submitted, you cannot edit until it is returned.')) return;
        setSubmitBusy(true);
        setActionError(null);
        try {
            await apiService.submitPaymentRequest(pr.paymentRequestId);
            setActionSuccess('Payment Request submitted to AP team.');
            await refresh();
        } catch (e: any) {
            setActionError(e?.response?.data?.message || e?.message || 'Submit failed');
        } finally {
            setSubmitBusy(false);
        }
    };

    const handleDiscardDraft = async () => {
        if (!pr || submitBusy) return;
        if (!window.confirm('Discard this draft? This cannot be undone.')) return;
        setSubmitBusy(true);
        setActionError(null);
        try {
            await apiService.deletePaymentRequest(pr.paymentRequestId);
            navigate('/payment-requests');
        } catch (e: any) {
            setActionError(e?.response?.data?.message || e?.message || 'Discard failed');
            setSubmitBusy(false);
        }
    };

    const [pdfBusy, setPdfBusy] = useState(false);
    const handlePrintPDF = async () => {
        if (!pr || pdfBusy) return;
        setPdfBusy(true);
        try {
            // Fetch via axios so the JWT auth interceptor runs; opening the URL
            // directly would 401 because top-level navigation doesn't carry the
            // Authorization header.
            await apiService.openPaymentRequestPdf(pr.paymentRequestId);
        } catch (e: any) {
            setActionError(e?.response?.data?.message || e?.message || 'Failed to load PDF');
        } finally {
            setPdfBusy(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render guards
    // -----------------------------------------------------------------------

    if (loading && !pr) {
        return (
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: '60vh' }}
                role="status"
                aria-live="polite"
            >
                <CircularProgress aria-label="Loading payment request" />
            </Stack>
        );
    }

    if (pageError && !pr) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }} role="alert">
                <Alert severity="error">{pageError}</Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate('/payment-requests')}>
                    Back to list
                </Button>
            </Box>
        );
    }

    if (!pr) {
        return null;
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Paper
            component="article"
            aria-labelledby="prd-heading"
            elevation={0}
            sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minHeight: 'calc(100vh - 100px)' }}
        >
            {/* Header strip */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Breadcrumbs sx={{ mb: 0.5 }} aria-label="Breadcrumb">
                        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
                            Home
                        </MuiLink>
                        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/payment-requests">
                            Payment Requests
                        </MuiLink>
                        <Typography color="text.primary" sx={{ wordBreak: 'break-word' }}>{pr.docRef}</Typography>
                    </Breadcrumbs>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 0.5, sm: 1.5 }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                        <Typography
                            component="h1"
                            id="prd-heading"
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: 18, sm: 22, md: 24 },
                                wordBreak: 'break-word',
                            }}
                        >
                            Payment Request {pr.docRef}
                        </Typography>
                        <PaymentRequestStatusChip status={pr.status} />
                    </Stack>
                </Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                    {/* Initiator actions on a Draft / Returned request. */}
                    {isInitiatorOfThisPR && (status === 'Draft' || status === 'Returned') && (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth={false}
                                sx={{ flex: { xs: 1, sm: 'unset' } }}
                                onClick={() => navigate(`/payment-requests/${pr.paymentRequestId}/edit`)}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ flex: { xs: 1, sm: 'unset' } }}
                                onClick={handleInitiatorSubmit}
                                disabled={submitBusy}
                            >
                                {submitBusy ? 'Submitting…' : 'Submit to AP Team'}
                            </Button>
                            {status === 'Draft' && (
                                <Button
                                    color="error"
                                    variant="outlined"
                                    sx={{ flex: { xs: 1, sm: 'unset' } }}
                                    onClick={handleDiscardDraft}
                                    disabled={submitBusy}
                                >
                                    Discard Draft
                                </Button>
                            )}
                        </>
                    )}
                    <Button
                        startIcon={<PrintIcon />}
                        variant="outlined"
                        sx={{ flex: { xs: 1, sm: 'unset' } }}
                        onClick={handlePrintPDF}
                        disabled={pdfBusy}
                        aria-label="Download Payment Request as PDF"
                    >
                        {pdfBusy ? 'Loading PDF…' : 'Print PDF'}
                    </Button>
                </Stack>
            </Stack>

            {/* Global action feedback */}
            {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                    {actionError}
                </Alert>
            )}
            {actionSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
                    {actionSuccess}
                </Alert>
            )}

            <Grid container spacing={2}>
                {/* ─── Main column ────────────────────────────────────────── */}
                <Grid item xs={12} md={8}>
                    {/* Initiator block (read-only) */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardHeader
                            title="Initiator Submission"
                            titleTypographyProps={{ variant: 'h6' }}
                            subheader="Read-only — submitted by initiator"
                        />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Allocation Type" value={pr.allocationType} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField
                                        label={
                                            pr.allocationType === 'Project'
                                                ? 'Project / Division'
                                                : 'Cost Center'
                                        }
                                        value={
                                            pr.allocationType === 'Project'
                                                ? `${pr.projectCode || '—'}${pr.divisionCode ? ` / ${pr.divisionCode}` : ''}`
                                                : pr.costCenterCode || '—'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField
                                        label="Vendor (CardCode)"
                                        value={pr.vendorCardCode}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Payment Type" value={pr.paymentType} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="PO DocEntry" value={pr.poDocEntry} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField
                                        label="GRPO Links"
                                        value={
                                            pr.grpoLinks && pr.grpoLinks.length > 0 ? (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    {pr.grpoLinks.map((g) => (
                                                        <Chip
                                                            key={g.paymentRequestGrpoId}
                                                            size="small"
                                                            label={`GRPO ${g.grpoDocNum ?? g.grpoDocEntry}`}
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                '—'
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Invoice Number" value={pr.invoiceNumber || '—'} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Invoice Date" value={formatDate(pr.invoiceDate)} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField
                                        label="Amount"
                                        value={
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {formatMoney(pr.amountFigures, pr.amountCurrency)}
                                            </Typography>
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Pay By" value={formatDate(pr.payBy)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ReadOnlyField
                                        label="Initiator Remarks"
                                        value={pr.initiatorRemarks || '—'}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Attachments — visible to all viewers; uploadable by
                        the appropriate stage role (initiator on Draft/Returned,
                        AP team on Submitted, admin via override). */}
                    <Box sx={{ mb: 2 }}>
                        <PaymentRequestAttachments
                            paymentRequestId={pr.paymentRequestId}
                            canEdit={attachmentsCanEdit}
                            defaultStage={attachmentsDefaultStage}
                        />
                    </Box>

                    {/* DocuSign envelope tracking — visible whenever an envelope exists. */}
                    {pr.docuSignEnvelopeId && (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardHeader
                                title="DocuSign Envelope"
                                titleTypographyProps={{ variant: 'h6' }}
                                subheader={
                                    docusignStatus?.envelope?.envelopeid
                                        ? `Envelope ID: ${docusignStatus.envelope.envelopeid}`
                                        : `Local envelope #${pr.docuSignEnvelopeId}`
                                }
                                action={
                                    <Button
                                        size="small"
                                        onClick={refreshDocuSignStatus}
                                        disabled={docusignLoading}
                                    >
                                        {docusignLoading ? 'Refreshing…' : 'Refresh'}
                                    </Button>
                                }
                            />
                            <Divider />
                            <CardContent>
                                {!docusignStatus && !docusignLoading && (
                                    <Typography variant="body2" color="text.secondary">
                                        No envelope details available yet.
                                    </Typography>
                                )}
                                {docusignStatus && (
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography variant="caption" color="text.secondary">
                                                Status:
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={docusignStatus.envelope?.status || 'unknown'}
                                                color={
                                                    docusignStatus.envelope?.status === 'completed'
                                                        ? 'success'
                                                        : docusignStatus.envelope?.status === 'declined' ||
                                                          docusignStatus.envelope?.status === 'voided'
                                                            ? 'error'
                                                            : 'info'
                                                }
                                            />
                                            {docusignStatus.envelope?.sentdate && (
                                                <Typography variant="caption" color="text.secondary">
                                                    sent {new Date(docusignStatus.envelope.sentdate).toLocaleString()}
                                                </Typography>
                                            )}
                                            {docusignStatus.envelope?.completeddate && (
                                                <Typography variant="caption" color="text.secondary">
                                                    completed {new Date(docusignStatus.envelope.completeddate).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Stack>

                                        {docusignStatus.envelope?.errormessage && (
                                            <Alert severity="warning" variant="outlined">
                                                {docusignStatus.envelope.errormessage}
                                            </Alert>
                                        )}

                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Signers (in routing order)
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(docusignStatus.signers || []).map((s: any) => {
                                                    const done =
                                                        s.status === 'completed' || s.status === 'signed';
                                                    const declined =
                                                        s.status === 'declined' || s.status === 'voided';
                                                    return (
                                                        <Stack
                                                            key={s.signerid}
                                                            direction="row"
                                                            spacing={1.5}
                                                            alignItems="center"
                                                            sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 26,
                                                                    height: 26,
                                                                    borderRadius: '50%',
                                                                    bgcolor: done
                                                                        ? 'success.main'
                                                                        : declined
                                                                            ? 'error.main'
                                                                            : 'grey.300',
                                                                    color: done || declined ? 'common.white' : 'text.secondary',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {s.routingorder}
                                                            </Box>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {s.signername}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    {s.signeremail}
                                                                </Typography>
                                                                {s.declinereason && (
                                                                    <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                                                        Declined: {s.declinereason}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Stack alignItems="flex-end" spacing={0.25}>
                                                                <Chip
                                                                    size="small"
                                                                    label={s.status || 'pending'}
                                                                    color={done ? 'success' : declined ? 'error' : 'default'}
                                                                />
                                                                {s.signeddate && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {new Date(s.signeddate).toLocaleString()}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                    );
                                                })}
                                                {(docusignStatus.signers || []).length === 0 && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No signers recorded.
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* AP Preparation block */}
                    {showAPPrep && (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardHeader
                                title="AP Team Preparation"
                                titleTypographyProps={{ variant: 'h6' }}
                                subheader="Add bank details, payment method and re-confirmation"
                            />
                            <Divider />
                            <CardContent>
                                {pr.bankFieldsLockedFromSAP && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Bank details are read-only — sourced from SAP vendor master.
                                    </Alert>
                                )}

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Invoice Details
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Invoice Number"
                                            value={apForm.invoiceNumber ?? ''}
                                            onChange={(e) =>
                                                setAPForm({ ...apForm, invoiceNumber: e.target.value })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Invoice Date"
                                            type="date"
                                            value={
                                                apForm.invoiceDate
                                                    ? String(apForm.invoiceDate).slice(0, 10)
                                                    : ''
                                            }
                                            onChange={(e) =>
                                                setAPForm({ ...apForm, invoiceDate: e.target.value || null })
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Bank Details
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Name"
                                            value={apForm.bankName ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankName: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Address"
                                            value={apForm.bankAddress ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankAddress: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Account No"
                                            value={apForm.bankAccountNo ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankAccountNo: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="IBAN"
                                            value={apForm.bankIBAN ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankIBAN: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="SWIFT"
                                            value={apForm.bankSWIFT ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankSWIFT: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Currency (3-letter)"
                                            value={apForm.bankCurrency ?? ''}
                                            inputProps={{ maxLength: 3 }}
                                            onChange={(e) =>
                                                setAPForm({ ...apForm, bankCurrency: e.target.value.toUpperCase() })
                                            }
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Email"
                                            value={apForm.bankEmail ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankEmail: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Tel"
                                            value={apForm.bankTel ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankTel: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bank Fax"
                                            value={apForm.bankFax ?? ''}
                                            onChange={(e) => setAPForm({ ...apForm, bankFax: e.target.value })}
                                            disabled={pr.bankFieldsLockedFromSAP}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Re-Confirmation
                                </Typography>
                                <FormControl sx={{ mb: 2 }}>
                                    <RadioGroup
                                        row
                                        value={apForm.reConfirmedYesNo}
                                        onChange={(e) =>
                                            setAPForm({
                                                ...apForm,
                                                reConfirmedYesNo: e.target.value as ReConfirmedYesNo,
                                            })
                                        }
                                    >
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel
                                            value="NotApplicable"
                                            control={<Radio />}
                                            label="Not Applicable"
                                        />
                                    </RadioGroup>
                                </FormControl>
                                {apForm.reConfirmedYesNo === 'Yes' && (
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                required
                                                label="Re-Confirmer Name"
                                                value={apForm.reConfirmerName ?? ''}
                                                onChange={(e) =>
                                                    setAPForm({ ...apForm, reConfirmerName: e.target.value })
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                required
                                                label="Re-Confirmer Emp ID"
                                                value={apForm.reConfirmerEmpID ?? ''}
                                                onChange={(e) =>
                                                    setAPForm({ ...apForm, reConfirmerEmpID: e.target.value })
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Payment Method
                                </Typography>
                                <FormControl sx={{ mb: 2 }}>
                                    <RadioGroup
                                        row
                                        value={apForm.paymentMethod}
                                        onChange={(e) =>
                                            setAPForm({
                                                ...apForm,
                                                paymentMethod: e.target.value as PaymentMethod,
                                            })
                                        }
                                    >
                                        <FormControlLabel value="Cash" control={<Radio />} label="Cash" />
                                        <FormControlLabel value="Cheque" control={<Radio />} label="Cheque" />
                                        <FormControlLabel value="Transfer" control={<Radio />} label="Transfer" />
                                    </RadioGroup>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    label="AP Remarks"
                                    inputProps={{ maxLength: 4000 }}
                                    value={apForm.apRemarks ?? ''}
                                    onChange={(e) => setAPForm({ ...apForm, apRemarks: e.target.value })}
                                    sx={{ mb: 2 }}
                                />

                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setReturnDialogOpen(true)}
                                        disabled={apSubmitting}
                                    >
                                        Return to Initiator
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleAPSubmit}
                                        disabled={apSubmitting}
                                    >
                                        {apSubmitting ? <CircularProgress size={20} /> : submitButtonLabel}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Post-to-SAP card */}
                    {showPostToSAP && (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardHeader
                                title="Ready to post to SAP"
                                titleTypographyProps={{ variant: 'h6' }}
                                subheader="Final approval received — push outgoing payment to SAP B1"
                            />
                            <Divider />
                            <CardContent>
                                {pr.sapPushLastError && (
                                    <Alert
                                        severity="error"
                                        sx={{ mb: 2 }}
                                        action={
                                            <Button
                                                color="inherit"
                                                size="small"
                                                onClick={handleRetrySAP}
                                                disabled={postingSAP}
                                            >
                                                Retry
                                            </Button>
                                        }
                                    >
                                        SAP push failed (attempts: {pr.sapPushAttempts}). {pr.sapPushLastError}
                                    </Alert>
                                )}
                                <Stack direction="row" spacing={1.5}>
                                    <Button
                                        variant="outlined"
                                        onClick={handlePreview}
                                        disabled={previewLoading}
                                    >
                                        {previewLoading ? <CircularProgress size={20} /> : 'Preview Payload'}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setPostConfirmOpen(true)}
                                        disabled={postingSAP}
                                    >
                                        Post to SAP
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Override panel (Admin) */}
                    {isAdmin && (
                        <Card variant="outlined" sx={{ mb: 2, borderColor: 'warning.main' }}>
                            <CardHeader
                                title="Admin Override"
                                titleTypographyProps={{ variant: 'h6' }}
                                subheader="Apply a single-field override (audit-logged)"
                                action={
                                    <IconButton
                                        size="small"
                                        onClick={() => setOverrideExpanded((v) => !v)}
                                        aria-label="toggle override panel"
                                    >
                                        {overrideExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                }
                            />
                            <Collapse in={overrideExpanded} unmountOnExit>
                                <Divider />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Field</InputLabel>
                                                <Select
                                                    value={overrideField}
                                                    label="Field"
                                                    onChange={(e) => setOverrideField(String(e.target.value))}
                                                >
                                                    {OVERRIDE_FIELDS.map((f) => (
                                                        <MenuItem key={f.value} value={f.value}>
                                                            {f.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={7}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="New Value"
                                                value={overrideValue}
                                                onChange={(e) => setOverrideValue(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                required
                                                multiline
                                                minRows={2}
                                                label="Reason"
                                                value={overrideReason}
                                                onChange={(e) => setOverrideReason(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Stack direction="row" justifyContent="flex-end">
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={handleOverrideSubmit}
                                                    disabled={overrideBusy}
                                                >
                                                    {overrideBusy ? <CircularProgress size={20} /> : 'Apply Override'}
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Collapse>
                        </Card>
                    )}

                    {/* Approver action panel */}
                    {showApproverPanel && (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardHeader
                                title="Approver Action"
                                titleTypographyProps={{ variant: 'h6' }}
                                subheader={`Acting as ${userRole}`}
                            />
                            <Divider />
                            <CardContent>
                                {docusignPending && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        DocuSign Pending — sign in your email.
                                        <Tooltip title="Check your email for the DocuSign envelope">
                                            <Button
                                                size="small"
                                                sx={{ ml: 1 }}
                                                startIcon={<OpenInNewIcon />}
                                            >
                                                Open in DocuSign
                                            </Button>
                                        </Tooltip>
                                    </Alert>
                                )}

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={6}
                                    label="Comments"
                                    value={approverComments}
                                    onChange={(e) => setApproverComments(e.target.value)}
                                    sx={{ mb: 2 }}
                                    helperText="Required for Reject/Return."
                                />

                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={handleApproverReturn}
                                        disabled={approverBusy}
                                    >
                                        Return
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleReject}
                                        disabled={approverBusy}
                                    >
                                        Reject
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={handleApprove}
                                        disabled={approverBusy}
                                    >
                                        {approverBusy ? <CircularProgress size={20} /> : 'Approve'}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* ─── Right rail ───────────────────────────────────────── */}
                <Grid item xs={12} md={4}>
                    {/* Status & Workflow */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardHeader title="Status & Workflow" titleTypographyProps={{ variant: 'subtitle1' }} />
                        <Divider />
                        <CardContent>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Current status
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <PaymentRequestStatusChip status={pr.status} />
                                    </Box>
                                </Box>
                                <ReadOnlyField label="Step" value={`${pr.currentStep} of 5`} />

                                {/* Resolved chain (server-computed against the current
                                    workflow config). Reflects threshold-skipping for
                                    THIS request's amount + currency. */}
                                {Array.isArray((pr as any).resolvedChain) && (pr as any).resolvedChain.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Approval chain
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            {((pr as any).resolvedChain as Array<any>).map((s) => (
                                                <Stack
                                                    key={s.stepNumber}
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    sx={{ opacity: s.skipped ? 0.55 : 1 }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 22, height: 22,
                                                            borderRadius: '50%',
                                                            bgcolor: s.skipped ? 'grey.300' : 'primary.main',
                                                            color: s.skipped ? 'text.secondary' : 'primary.contrastText',
                                                            display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 12, fontWeight: 700,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {s.stepNumber}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                textDecoration: s.skipped ? 'line-through' : 'none',
                                                            }}
                                                        >
                                                            {s.stageName || s.approverRole}
                                                        </Typography>
                                                        {s.skipped && s.skipReason && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Skipped — {s.skipReason}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* History timeline placeholder */}
                    {/* TODO Phase 21: fetch and render history timeline. */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardHeader title="History" titleTypographyProps={{ variant: 'subtitle1' }} />
                        <Divider />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                (History will appear here.)
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Linked SAP Documents */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardHeader
                            title="Linked SAP Documents"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                        />
                        <Divider />
                        <CardContent>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        PO
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                        <Typography variant="body2">DocEntry {pr.poDocEntry}</Typography>
                                        <Tooltip title="Open in SAP (Phase 21)">
                                            <span>
                                                <Button
                                                    size="small"
                                                    startIcon={<OpenInNewIcon />}
                                                    disabled
                                                >
                                                    Open in SAP
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                                {pr.grpoLinks && pr.grpoLinks.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            GRPOs
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                            {pr.grpoLinks.map((g) => (
                                                <Chip
                                                    key={g.paymentRequestGrpoId}
                                                    size="small"
                                                    label={`GRPO ${g.grpoDocNum ?? g.grpoDocEntry}`}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                {pr.sapOutgoingPaymentDocNum && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            SAP Outgoing Payment
                                        </Typography>
                                        <Typography variant="body2">{pr.sapOutgoingPaymentDocNum}</Typography>
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ─── Return-to-Initiator dialog ───────────────────────── */}
            <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Return to Initiator</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Comments are required and will be visible to the initiator.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        label="Comments"
                        value={returnComments}
                        onChange={(e) => setReturnComments(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleReturn}
                        disabled={apSubmitting}
                    >
                        Return
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ─── SAP Preview dialog ───────────────────────── */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>SAP Outgoing Payment Payload</DialogTitle>
                <DialogContent>
                    <Box
                        component="pre"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            bgcolor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            maxHeight: '60vh',
                        }}
                    >
                        {previewPayload ? JSON.stringify(previewPayload, null, 2) : '—'}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ─── Post-to-SAP confirm dialog ───────────────────────── */}
            <Dialog open={postConfirmOpen} onClose={() => setPostConfirmOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Post to SAP?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will create an Outgoing Payment in SAP B1 and mark this Payment Request as Executed.
                        Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPostConfirmOpen(false)} disabled={postingSAP}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePostToSAP}
                        disabled={postingSAP}
                    >
                        {postingSAP ? <CircularProgress size={20} /> : 'Confirm & Post'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default PaymentRequestDetail;
